import { Card, Col, Row, Space } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Modal from 'antd/lib/modal/Modal';
import { Field, Formik, FormikActions, FormikProps } from 'formik';
import {
	FCheckbox,
	FFieldLabel,
	FInputNumber,
	FRangePicker,
	FTextArea,
} from 'forms/FormikFormItems';
import { ajaxByUser, ajaxCatch } from 'helper/api';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { getProfiBazaApiClient } from 'services/ProfiBazaApi';
import { NotificationOccurrenceDto } from 'services/src/models';

import { UpdateNotificationOccurrenceCommand } from './commands/UpdateNotificationOccurrenceCommandMapper';
import { UserPrivateOccurrenceValidationSchema } from './validators/UserPrivateOccurrenceValidationSchema';

interface IProps {
	onCreate: () => void;
}

interface IState {
	visible: boolean;
	occurrenceId: string | undefined;
	occurrence: NotificationOccurrenceDto | undefined;
}

export interface IEditUserPrivateOccurrenceModal {
	showModal: (occurrenceId: string) => void;
}

const EditUserPrivateOccurrenceModal = React.forwardRef<
	IEditUserPrivateOccurrenceModal,
	IProps
>((props, ref) => {
	const [state, setState] = useState<IState>({
		occurrence: undefined,
		visible: false,
		occurrenceId: undefined,
	});

	useImperativeHandle(ref, () => ({
		showModal: (occurrenceId: string) => {
			setState({
				...state,
				visible: true,
				occurrenceId: occurrenceId,
			});
		}
	}));

	useEffect(() => {
		if (state.occurrenceId) {
			ajaxCatch(() =>
				getProfiBazaApiClient().then((api) =>
					api.notificationOccurrence
						.getById(state.occurrenceId!)
						.then((result) => {
							setState({
								...state,
								occurrence: result,
							});
						})
				)
			);
		}
	}, [state.occurrenceId!]);

	return (
		<Formik
			validateOnChange={true}
			validateOnBlur={false}
			initialValues={
				new UpdateNotificationOccurrenceCommand(state.occurrence)
			}
			enableReinitialize
			validationSchema={UserPrivateOccurrenceValidationSchema}
			onSubmit={(
				values: UpdateNotificationOccurrenceCommand,
				actions: FormikActions<UpdateNotificationOccurrenceCommand>
			) => {
				ajaxByUser(
					'Edytowano zdarzenie prywatne',
					() =>
						getProfiBazaApiClient().then((api) =>
							api.notificationOccurrence.update({
								body: UpdateNotificationOccurrenceCommand.toUpdateCommand(
									values
								),
							})
						),
					() => {
						setState({
							occurrenceId: undefined,
							visible: false,
							occurrence: undefined,
						});
						
						props.onCreate();
						actions.resetForm();
					},
					(errors) => {
						actions.setErrors(errors);
					}
				).then(() => actions.setSubmitting(false));
			}}
			render={(
				props: FormikProps<UpdateNotificationOccurrenceCommand>
			) => (
				<Modal
					visible={state.visible}
					maskClosable={false}
					centered
					title="Edytuj zdarzenie"
					closable={false}
					onOk={() => {
						if (!props.isSubmitting) {
							props.submitForm();
						} 
					}}
					onCancel={() => {
						setState({
							...state,
							visible: false,
						});
						props.resetForm();
					}}
					okButtonProps={{ disabled: props.isSubmitting }}
					cancelButtonProps={{ disabled: props.isSubmitting }}
					cancelText="Anuluj"
					okText="Zapisz"
					destroyOnClose
				>
					<Row>
						<Col span={16}>
							<FFieldLabel label={'Data zdarzenia'} />
							<Field
								component={FRangePicker}
								style={{
									width: '100%',
								}}
								inputReadOnly={false}
								name="validDateRange"
							/>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<FFieldLabel label="Tekst powiadomienia" />
							<Field
								component={FTextArea}
								label="Tekst powiadomienia"
								rows={4}
								placeholder="Treść powiadomienia"
								name="header"
								autoFocus={true}
							/>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<FFieldLabel label="Terminy wysyłki powiadomienia" />
							<Card>
								<Row>
									<Col span={8}>
										<Space>
											<Field
												label="7 dni przed"
												component={FCheckbox}
												name="triggeredWeekBefore"
												id="triggeredWeekBefore"
											/>
											<FFieldLabel
												label="7 dni przed"
												for="triggeredWeekBefore"
											/>
										</Space>
									</Col>
									<Col span={8}>
										<Space>
											<Field
												label="1 dzień przed"
												component={FCheckbox}
												name="triggeredDayBefore"
												id="triggeredDayBefore"
											/>
											<FFieldLabel
												label="1 dzień przed"
												for="triggeredDayBefore"
											/>
										</Space>
									</Col>

									<Col span={8}>
										<Space>
											<Field
												label="w dniu zdarzenia"
												component={FCheckbox}
												name="triggeredExactDay"
												id="triggeredExactDay"
											/>
											<FFieldLabel
												label="w dniu zdarzenia"
												for="triggeredExactDay"
											/>
										</Space>
									</Col>
								</Row>
								<Row>
									<Col span={10}>
										<Space>
											<Field
												component={FCheckbox}
												name="customTriggerActivation"
												id="customTriggerActivation"
												onChange={(
													value: CheckboxChangeEvent
												) => {
													if (!value.target.checked) {
														props.setFieldValue(
															'customTrigger',
															undefined
														);
													}
												}}
											/>
											<Field
												component={FInputNumber}
												disabled={
													!props.values[
														'customTriggerActivation'
													]
												}
												size="small"
												precision={0}
												style={{ width: 50 }}
												name="customTrigger"
												label="Inna liczba dni"
											/>
											<FFieldLabel
												label="dni przed"
												for="customTriggerActivation"
											/>
										</Space>
									</Col>
								</Row>
							</Card>
						</Col>
					</Row>
				</Modal>
			)}
		/>
	);
});

export default EditUserPrivateOccurrenceModal;
